import { WarningOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Progress, Tooltip, Typography } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  BOT_TYPE_STYLES,
  DEFAULT_BOT_TYPE_STYLE,
} from '@app/components/Modals/BotDetailsModal/constants/botColors';
import {
  INCOMING_MESSAGE_TRIGGER_TYPE,
  SET_TAG_ROBOT_INDEX,
  TRANSITION_FROM_THE_SALES_FUNNEL_ROBOT_INDEX,
  TRANSITION_FROM_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX,
  TRANSITION_TO_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX,
} from '@app/components/Modals/BotDetailsModal/constants/botIndexes';
import { SCAN_PERIOD_OPTIONS } from '@app/components/Modals/BotDetailsModal/data/scanPeriod';
import { SOCIAL_CHANNEL_TITLES } from '@app/components/Modals/BotDetailsModal/data/socialIntegrations';
import type { RobotTagFilterDto } from '@app/components/Modals/BotDetailsModal/types';
import { formatTriggerTagFilters } from '@app/components/Modals/BotDetailsModal/utils/triggerTagsGroups';
import {
  CLIENT_STATUSES,
  LOGS_REGISTRY_PATH,
  PREFIX,
  robotTriggers,
} from '@app/constants';
import '@pages/SalesFunnels/EditSalesFunnel/styles/EditSalesFunnelRobotComponent.desktop.scss';
import type { NFunnels } from '@app/types';

import { getRobotIcon } from '@pages/SalesFunnels/EditSalesFunnel/utils';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

const { Text } = Typography;

type Props = {
  robot: NFunnels.SalesFunnelRobotDto;
  onClick?: () => void;
  robotsWithProgress: NFunnels.RobotTaskGroupProgressEventType[];
};

export const EditSalesFunnelRobotComponent: React.FC<Props> = ({
  robot,
  onClick,
  robotsWithProgress,
}) => {
  const [searchParams] = useSearchParams();
  const funnelSaleId = useMemo(() => searchParams.get('id'), [searchParams]);
  const navigate = useNavigate();

  const {
    RobotTypeName,
    TriggerInfo,
    TriggerType,
    Id,
    Comment,
    CountUnreadLogs,
    Type,
  } = robot;

  const clientStatus = useMemo(
    () =>
      CLIENT_STATUSES.find(({ id }) => id === TriggerInfo?.RecordStatus)?.label,
    [TriggerInfo?.RecordStatus]
  );

  const services = useMemo(() => TriggerInfo?.ClientServices, [TriggerInfo]);

  const scanPeriodLabel = useMemo(
    () =>
      SCAN_PERIOD_OPTIONS.find(({ value }) => value === TriggerInfo?.ScanPeriod)
        ?.label,
    [TriggerInfo]
  );

  const trigger = useMemo(() => {
    const isTagTriggerRobot = [
      TRANSITION_TO_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX,
      TRANSITION_FROM_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX,
      TRANSITION_FROM_THE_SALES_FUNNEL_ROBOT_INDEX,
      SET_TAG_ROBOT_INDEX,
    ].includes(String(Type));

    if (TriggerType === 'TagIsSet' && isTagTriggerRobot) {
      return { label: TriggerType, title: 'Смена тега лида' };
    }

    if (
      TriggerType === INCOMING_MESSAGE_TRIGGER_TYPE &&
      String(Type) === SET_TAG_ROBOT_INDEX
    ) {
      return {
        label: TriggerType,
        title: 'Ключевые слова во входящем сообщении',
      };
    }

    return robotTriggers.find(({ label }) => label === TriggerType);
  }, [TriggerType, Type]);

  const tags = useMemo(() => TriggerInfo?.TagNames, [TriggerInfo]);

  // Сводка триггера IncomingMessage робота «Установка тега»
  const incomingMessageInfo = useMemo(() => {
    if (
      TriggerType !== INCOMING_MESSAGE_TRIGGER_TYPE ||
      String(Type) !== SET_TAG_ROBOT_INDEX ||
      !TriggerInfo
    ) {
      return null;
    }

    const socialType =
      typeof TriggerInfo.SocialType === 'string' ? TriggerInfo.SocialType : '';
    const keywordGroupsCount = Number(TriggerInfo.KeywordGroupsCount) || 0;

    return {
      channel: socialType
        ? SOCIAL_CHANNEL_TITLES[socialType] ?? socialType
        : 'Любой канал',
      keywords: keywordGroupsCount
        ? `Строк условий: ${keywordGroupsCount}`
        : 'Любое сообщение',
    };
  }, [TriggerInfo, TriggerType, Type]);

  // Новый формат TagIsSet; у legacy-роботов массив пустой — для них
  // остаются TagNames + HasOrRelation.
  const tagFiltersLabel = useMemo(() => {
    const tagFilters: unknown = TriggerInfo?.TagFilters;

    return Array.isArray(tagFilters) && tagFilters.length
      ? formatTriggerTagFilters(tagFilters as RobotTagFilterDto[])
      : null;
  }, [TriggerInfo]);

  const robotProgress = useMemo(
    () => robotsWithProgress.find(({ robotId }) => robotId === Id),
    [Id, robotsWithProgress]
  );

  const showUnreadLog = Boolean(CountUnreadLogs && Number(CountUnreadLogs) > 0);

  const handleLogsIconClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(LOGS_REGISTRY_PATH, {
        state: { salesFunnelId: funnelSaleId, robotId: Id },
      });
    },
    [Id, funnelSaleId, navigate]
  );

  const renderInfoLine = (label: string, value: React.ReactNode) => (
    <div className="info-line">
      <Text className="robot-info__label">{label}:</Text>
      <Tooltip title={value}>
        <Text className="short-text">{value}</Text>
      </Tooltip>
    </div>
  );

  return (
    <div
      className={`${PREFIX}edit-sales-funnel-robot-component ${
        BOT_TYPE_STYLES[Type] || DEFAULT_BOT_TYPE_STYLE
      }`}
      onClick={onClick}
    >
      <Flex gap={2}>
        <Flex vertical align="center" gap={6} className="icon-column">
          <FontAwesomeIcon
            icon={getRobotIcon(Type)}
            size="sm"
            className="icon"
          />

          {showUnreadLog && (
            <Tooltip title="Есть непрочитанные уведомления. Кликните для просмотра.">
              <WarningOutlined
                className="logs-icon"
                onClick={handleLogsIconClick}
              />
            </Tooltip>
          )}
        </Flex>

        <Flex vertical flex={1} className="content-column">
          <Tooltip title={RobotTypeName}>
            <Text strong className="robot-name short-text">
              {RobotTypeName}
            </Text>
          </Tooltip>

          <div className="robot-info">
            {Comment && renderInfoLine('Комментарий', Comment)}
            {trigger && renderInfoLine('Триггер', trigger.title)}
            {clientStatus && renderInfoLine('Статус записи', clientStatus)}

            {incomingMessageInfo && (
              <>
                {renderInfoLine('Ключевые слова', incomingMessageInfo.keywords)}
                {renderInfoLine('Канал', incomingMessageInfo.channel)}
              </>
            )}

            {Array.isArray(services) &&
              renderInfoLine('Услуги', services.join(', '))}

            {scanPeriodLabel &&
              renderInfoLine('Периодичность сканирования', scanPeriodLabel)}

            {tagFiltersLabel && renderInfoLine('Теги', tagFiltersLabel)}

            {!tagFiltersLabel &&
              Array.isArray(tags) &&
              renderInfoLine('Теги', tags.join(', '))}

            {!tagFiltersLabel &&
              TriggerInfo &&
              'HasOrRelation' in TriggerInfo &&
              renderInfoLine(
                'Связь между тегами',
                TriggerInfo.HasOrRelation ? 'ИЛИ' : 'И'
              )}

            {robotProgress && robotProgress.progress !== 100 && (
              <div className="progress-section">
                <Text className="robot-info__label">Прогресс:</Text>
                <Flex gap={8} align="center">
                  <Progress
                    percent={robotProgress.progress}
                    size="small"
                    status={robotProgress.hasErrors ? 'exception' : 'active'}
                    style={{ flex: 1 }}
                  />
                  {robotProgress.hasErrors && (
                    <Tooltip title="При выполнении возникли ошибки">
                      <FontAwesomeIcon
                        icon={'fa-solid fa-circle-exclamation' as IconProp}
                        size="lg"
                        className="error-icon"
                      />
                    </Tooltip>
                  )}
                </Flex>
              </div>
            )}
          </div>
        </Flex>
      </Flex>
    </div>
  );
};
